import type { ReactNode } from 'react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import type { ValueDto } from '@/services/ws-search/src/main/resources/rest-api';
import { Typography } from '@/js/libs/ui';

import { SearchListItem } from './SearchListItem.tsx';
import { BlockTitleWrapper, BlockWrapper, ShowMoreWrapper, UnavailableWrapper } from './styles.ts';

type Props = {
  title: string;
  total: number;
  icon: ReactNode;
  items: ValueDto[];
  search: string;
  onItemClick: (id: ValueDto['id']) => void;
  onShowMoreClick: () => void;
  highlightColor: string;
  dataTestId?: string;
  failed?: boolean;
};

export const SearchListBlock = memo(function SearchListBlock({
  icon,
  items,
  title,
  total,
  onItemClick,
  onShowMoreClick,
  search,
  highlightColor,
  dataTestId,
  failed,
}: Props) {
  const [t] = useTranslation(['backend', 'common']);
  const { colors } = useTheme();

  return (
    <BlockWrapper data-testid={dataTestId}>
      <BlockTitleWrapper>
        {icon}
        <Typography.TextMedium>{failed ? title : `${title} (${total})`}</Typography.TextMedium>
      </BlockTitleWrapper>
      {failed ? (
        <UnavailableWrapper data-testid='search-block-unavailable'>
          <Typography.TextMedium color={colors.grey100_1}>
            {t('common:SEARCH_RESULTS_UNAVAILABLE', 'Currently unavailable')}
          </Typography.TextMedium>
        </UnavailableWrapper>
      ) : (
        <>
          {items.map((v) => (
            <SearchListItem
              key={v.id}
              item={v}
              search={search}
              onClick={onItemClick}
              highlightColor={highlightColor}
              dataTestId='global-search-item'
            />
          ))}
          {total > 10 && (
            <ShowMoreWrapper data-testid='show-all-button'>
              <a onClick={onShowMoreClick}>{t('common:SHOW_ALL')}</a>
            </ShowMoreWrapper>
          )}
        </>
      )}
    </BlockWrapper>
  );
});
